// ============================================================================
// TypeScript mirror of the NEW unified reporting engine's contracts.
// Source of truth: Distributed-Systems/Ronin.Infrastructure/Contracts/Reports/
//   UnifiedReportContracts.cs
//
// The API serializes all enums as STRINGS (JsonStringEnumConverter), and the
// `values` / `totals` dictionaries are keyed by the PascalCase MetricKey name
// (dictionary keys are NOT camelCased). All money metrics are integer CENTS.
// ============================================================================

export type ReportType =
  | "SalesByEvent"
  | "SalesByVendor"
  | "SalesByLocation"
  | "SalesByVendorType"
  | "TaxByEvent"
  | "EventSummary"
  | "SalesByDay"
  | "SalesByHour"
  | "CumulativeHourly"
  // Ticketing Net Hourly (RP-2175) — SalesByHour / CumulativeHourly restricted to the ticketing channel
  // (OrderType = Ticketing, forced server-side). Matches the legacy "Ticketing Net Hourly Sales Report".
  | "TicketingNetHourly"
  | "TicketingCumulativeHourly"
  | "TopItems"
  | "ProductMixByItem"
  | "ProductMixByCategory"
  | "PaymentMix"
  | "TransactionsList"
  | "OrderItemsList"
  // Tab Management (RP-2166) — one row per POS TAB order across every status (open/in-use/deleted/closed).
  | "TabManagementList"
  // Web Checkout (RP-2182) — one row per legacy web-checkout order. Those rows migrated to the Invoice
  // entity (InvoiceType == "Web Checkout"), NOT Order; the engine reads Invoices. Matches the legacy
  // admin-app "Web Checkout Report" (13 of 14 columns; legacy reference_id was not migrated).
  | "WebCheckoutList"
  // Commissary (RP-2173) — one row per commissary order (OrderType = Commissary, forced server-side),
  // vendor-grouped with expandable line items. Matches the legacy "Commissary Report" (order_commissary).
  // Commissary is EXCLUDED from every sales report engine-side; this list is its only reporting surface.
  | "CommissaryList"
  // Consolidated Financial report grouped by a dimension (Event|Vendor|Location|VendorType|Day).
  | "FinancialByDimension"
  // Ticketing Financial (RP-2174) — FinancialByDimension restricted to the ticketing channel, with the
  // legacy 11-column decompositions. Default group-by = Day; ticketing OrderType forced server-side.
  | "TicketingFinancial"
  // Sprint-2 reports (feature/reporting-queue-sprint2)
  | "CardBrandBreakdown"
  | "DiscountUsage";

export type ReportGrain = "Order" | "Item" | "Transaction";

export type ReportDimension =
  | "Event"
  | "Vendor"
  | "Location"
  | "VendorType"
  | "Day"
  | "Clerk"
  | "Hour"
  | "Item"
  | "Category"
  | "Tender"
  | "None";

export type ReportShape = "KeyedRollup" | "TimeSeries" | "Composite" | "DetailList";

// Every metric the engine can carry. Money metrics are CENTS; counts are integers.
export type MetricKey =
  | "OrderCount"
  | "Gross"
  | "Tax"
  | "Tip"
  | "Surcharge"
  | "Units"
  | "ItemRevenue"
  | "ItemTax"
  | "ItemDiscount"
  | "TransactionCount"
  | "TransactionAmount"
  | "NetSales"
  | "Discount"
  | "Refunds"
  // Item-grain gross (item revenue + item tax) — ProductMix only (engine returns row.gross).
  | "ItemGross"
  // Per-tender breakdown on FinancialByDimension (RP-2153/54/55/57/59) — $ amount (money-as-tendered,
  // like the legacy *_charged buckets) AND approved-transaction count per tender class, on every row
  // and in totals. Contract: docs/Reports-API-Contract-Sprint2-QA.md §1 (Distributed-Systems).
  | "TenderCreditAmount"
  | "TenderCreditCount"
  | "TenderCashAmount"
  | "TenderCashCount"
  | "TenderCashBalanceAmount"
  | "TenderCashBalanceCount"
  | "TenderRfidAmount"
  | "TenderRfidCount"
  | "TenderPromoBalanceAmount"
  | "TenderPromoBalanceCount"
  | "TenderTokenAmount"
  | "TenderTokenCount"
  | "TenderOtherAmount"
  | "TenderOtherCount"
  // Financial DECOMPOSITIONS (RP-2174 Ticketing Financial) — additive splits that reconcile to the
  // canonical metrics (they never redefine Gross/Net/Discount/Refunds/Tip). All money (cents).
  //   DiscountExPromo + PromoDiscount == Discount ; Σ Tender*Tip == Tip ; 0 ≤ RefundsPreTax ≤ Refunds.
  //   MerchantProcessed = TenderCredit/RfidAmount + TenderCredit/RfidTip + Surcharge − Discount − Refunds.
  | "DiscountExPromo"
  | "PromoDiscount"
  | "RefundsPreTax"
  | "MerchantProcessed"
  | "TenderCreditTip"
  | "TenderCashTip"
  | "TenderCashBalanceTip"
  | "TenderRfidTip"
  | "TenderPromoBalanceTip"
  | "TenderTokenTip"
  | "TenderOtherTip"
  // Untendered sales portion (no approved tender covered it) — Σ Tender*Amount + Untendered == Gross.
  | "TenderUntenderedAmount"
  // Legacy Vendor Sales / Vendor Type Sales "Net Deduction - Promo Balance Sales" (RP-2155/2159) —
  // the promo-balance-funded share of sales the legacy proc subtracted when building Total Net Sales
  // (its net_promo_balance_sales column). NAME NOT YET CONFIRMED against the engine build in flight;
  // every consumer filters on data.metrics, so a mismatch just leaves the column out.
  | "NetPromoBalanceSales";

// Metrics that are integer counts (NOT money) — everything else is cents.
export const COUNT_METRICS: ReadonlySet<MetricKey> = new Set<MetricKey>([
  "OrderCount",
  "Units",
  "TransactionCount",
  "TenderCreditCount",
  "TenderCashCount",
  "TenderCashBalanceCount",
  "TenderRfidCount",
  "TenderPromoBalanceCount",
  "TenderTokenCount",
  "TenderOtherCount",
]);

export interface ReportQueryParams {
  reportType: ReportType;
  from?: string; // yyyy-MM-dd
  to?: string; // yyyy-MM-dd
  eventId?: string; // GUID (new-system id)
  vendorId?: string; // GUID
  locationId?: string; // GUID
  limit?: number;
  // FinancialByDimension group-by: Event | Vendor | Location | VendorType | Day.
  dimension?: ReportDimension;
}

export interface WindowInfo {
  from: string; // yyyy-MM-dd
  to: string; // yyyy-MM-dd
  dayBoundary: string; // e.g. "5 AM local business day (America/Chicago)"
  timeZone: string; // IANA zone or "UTC"
}

export interface ReportRow {
  key: string | null; // dimension id (null for "no event" etc.); "yyyy-MM-dd:HH" on by-day hourly rows
  label: string; // display text
  values: Partial<Record<MetricKey, number>>; // metric -> cents/units; may carry MORE than metrics[] (suppressed-but-available)
  group: string | null; // optional secondary label (an item's Category; the day key on by-day hourly rows)
}

export interface ReportResponse {
  type: ReportType;
  grain: ReportGrain;
  dimension: ReportDimension;
  shape: ReportShape;
  window: WindowInfo;
  // Ordered list of columns the report ADVERTISES for display. rows[].values may carry extra keys
  // that are computed but suppressed (e.g. Gross on CumulativeHourly) — opt in explicitly to show them.
  metrics: MetricKey[];
  rows: ReportRow[];
  totals: Partial<Record<MetricKey, number>>;
}

// ---------------------------------------------------------------------------
// Row-level detail (reconciliation export) — GET /reports/detail.
// Mirror of Distributed-Systems Contracts/Reports/DetailListContracts.cs.
// All money fields are integer CENTS; enums serialize as strings; props camelCase.
// ---------------------------------------------------------------------------

export type DetailReportType = "TransactionsList" | "OrderItemsList";

// One row per transaction, with its order's context. Gross/Net are ORDER-level and land on the
// anchor row (isOrderAnchor) so each column sums to the order total exactly once.
export interface TransactionDetailRow {
  // identity / scope
  orderId: string;
  orderLegacyId: number | null;
  transactionId: string | null; // null = synthetic row for a settled order with NO tender
  eventId: string | null;
  eventName: string | null;
  vendorId: string | null;
  locationId: string | null;
  // day assignment
  businessDay: string; // yyyy-MM-dd
  dayBoundary: string;
  timeZone: string;
  transactionAtUtc: string | null; // ISO UTC
  // status / tender
  orderStatus: string;
  refundKind: string | null;
  transactionStatus: string;
  tender: string | null;
  isRealMoney: boolean;
  // order money ledger (cents)
  subtotal: number;
  tax: number;
  tip: number;
  surcharge: number;
  lineSubtotal: number;
  lineDiscount: number;
  orderDiscount: number;
  refundGross: number;
  realMoneyCollected: number;
  // this transaction's amount
  transactionAmount: number;
  // contributions (Σ over included rows == Totals)
  grossContribution: number;
  netContribution: number;
  transactionCountContribution: number;
  transactionAmountContribution: number;
  refundsContribution: number;
  // inclusion
  isOrderAnchor: boolean;
  included: boolean;
  inclusionReason: string;
}

// One row per order item on a settled sale (+ excluded items flagged).
export interface OrderItemDetailRow {
  orderId: string;
  orderItemId: string;
  itemId: string | null;
  name: string;
  eventId: string | null;
  vendorId: string | null;
  businessDay: string; // yyyy-MM-dd
  dayBoundary: string;
  transactionAtUtc: string | null;
  orderStatus: string;
  quantity: number;
  unitPrice: number;
  lineSubtotal: number;
  lineTax: number;
  lineDiscount: number;
  unitsContribution: number;
  itemRevenueContribution: number;
  itemTaxContribution: number;
  itemDiscountContribution: number;
  // Applied modifiers on the line (Square-style / inventory-balancing). OPTIONAL — present once the
  // reporting-backlog-assess engine deploys; sparse in prod until the modifier backfill runs.
  modifierCount?: number;
  modifierTotal?: number; // cents
  modifierSummary?: string | null;
  included: boolean;
  inclusionReason: string;
}

// One row per POS TAB order (Order.IsTab), across EVERY status — matches the legacy admin-app
// "Tab Management Report". Money fields are integer CENTS. Subtotal/Tax/Tip/Surcharge are the RAW
// per-order stored fields; Discount is the canonical Σ line + whole-order discount. transactionAtUtc
// is null for an open/unpaid tab. Mirror of Distributed-Systems DetailListContracts.TabManagementRow.
export interface TabManagementRow {
  orderId: string;
  orderLegacyId: number | null; // legacy orders.id — the "Order ID" ops recognize
  eventId: string | null;
  eventName: string | null;
  vendorId: string | null;
  locationId: string | null;
  username: string | null; // Order.Cashier.UserName
  orderStatus: string; // new OrderStatus enum (legacy tab lifecycle collapsed at migration — see page note)
  tabName: string | null; // Order.CustomerName
  tabNickname: string | null; // Order.CustomerNickname
  cardLastFour: string | null; // most-recent active saved-card last 4 for the attendee
  subtotal: number;
  tax: number;
  tip: number;
  surcharge: number; // Service Fee
  discount: number; // canonical Σ LineDiscount + Σ OrderDiscount
  grandTotal: number;
  transactionAtUtc: string | null; // ISO UTC — paid time; null for an open tab
  createdAtUtc: string;
  updatedAtUtc: string;
  items: string; // "Name: X - Qty: N - Price: $Y | …"
}

interface DetailListResponse<T> {
  type: ReportType;
  window: WindowInfo;
  rowCount: number;
  truncated: boolean; // rows were capped by `limit`; totals still cover the full set
  rows: T[];
  totals: Partial<Record<MetricKey, number>>;
}

// One row per legacy web-checkout order (RP-2182) — matches the legacy admin-app "Web Checkout Report".
// The rows migrated to Invoice (InvoiceType == "Web Checkout"), NOT Order. Money fields are integer CENTS
// and are the RAW migrated Invoice fields (subtotal/serviceFee/totalAmount = legacy amount/service_fee/
// total_amount) — no Net/Gross reinterpretation. `referenceId` is ALWAYS null (legacy reference_id was not
// migrated — disclosed). `selection` is the migrator-merged "selector: selection" line-item description.
// `transactionDateUtc` is the legacy created_at (raw UTC); the legacy grid displayed it minus 5h (a
// hardcoded EST offset) — the page carries that display convention for parity. Mirror of Distributed-Systems
// DetailListContracts.WebCheckoutRow.
export interface WebCheckoutRow {
  // identity / scope (bonus, appended after the legacy columns)
  invoiceId: string;
  legacyId: number | null; // legacy web_checkout_orders.id
  eventId: string | null;
  eventName: string | null;
  vendorId: string | null;
  // the 14 legacy columns, in legacy display order
  transactionDateUtc: string; // ISO UTC — legacy created_at
  referenceId: string | null; // GAP: always null (legacy reference_id not migrated)
  processorReferenceId: string | null;
  selection: string | null; // legacy showed `selection` only; this is the merged "selector: selection"
  totalAmount: number; // legacy total_amount
  subtotal: number; // legacy amount
  serviceFee: number; // legacy service_fee
  clientName: string;
  clientEmail: string | null;
  clientPhone: string | null;
  billingName: string;
  billingAddress: string;
  cardType: string | null;
  cardLastFour: string | null;
}

export type TransactionsListResponse = DetailListResponse<TransactionDetailRow>;
export type OrderItemsListResponse = DetailListResponse<OrderItemDetailRow>;
export type TabManagementListResponse = DetailListResponse<TabManagementRow>;
export type WebCheckoutListResponse = DetailListResponse<WebCheckoutRow>;

// ---- Commissary (RP-2173) — DetailListContracts.Commissary* ----------------------------------------
// One row per commissary order, every workflow status except the legacy "adjustment" rows. Money fields
// are integer CENTS, shown verbatim (raw stored order fields — commissary is NOT a sale, so there is no
// Net/Gross here by design). The per-vendor and grand money totals are FULFILLED-only (the legacy page's
// rule) and computed SERVER-side — the page renders them, it does not sum money itself.
export interface CommissaryLineRow {
  name: string; // legacy items[].item_name
  quantity: number;
  unitPrice: number; // cents
  lineTotal: number; // cents (legacy items[].total_price)
  notes: string | null;
}

export interface CommissaryOrderRow {
  orderId: string;
  legacyId: number | null; // legacy order_commissary.id — the "Order #" ops recognize
  eventId: string | null;
  eventName: string | null;
  vendorId: string;
  vendorName: string;
  pocName: string | null; // GAP: legacy vendor.poc_name has no new-schema column → always null
  pocPhone: string | null; // GAP: legacy vendor.poc_phone → always null
  status: string; // commissary workflow status verbatim (pending/approved/rejected/fulfilled)
  placedBy: string | null; // legacy user first+last name
  subtotal: number; // cents, verbatim
  tax: number; // cents, verbatim
  total: number; // cents, verbatim (legacy `total`)
  notes: string | null;
  organizerNotes: string | null;
  rejectionReason: string | null;
  fulfilledAt: string | null; // ISO UTC
  createdAtUtc: string; // ISO UTC — legacy created_at (sort key, desc)
  updatedAtUtc: string;
  items: CommissaryLineRow[];
}

export interface CommissaryVendorTotal {
  vendorId: string;
  vendorName: string;
  pocName: string | null; // GAP — see above
  pocPhone: string | null; // GAP — see above
  orderCount: number; // every listed order for the vendor
  fulfilledSubtotal: number; // cents — FULFILLED orders only (legacy rule)
  fulfilledTax: number;
  fulfilledTotal: number;
}

export interface CommissaryMoneyTotals {
  fulfilledSubtotal: number; // cents — FULFILLED-only grand sums (server-computed)
  fulfilledTax: number;
  fulfilledTotal: number;
}

export interface CommissaryListResponse extends DetailListResponse<CommissaryOrderRow> {
  vendorTotals: CommissaryVendorTotal[];
  moneyTotals: CommissaryMoneyTotals;
}

// Slim event lookup for the filter dropdown — /events/options.
export interface EventOption {
  id: string; // GUID
  name: string;
}

// /vendors/options and /locations/options — same {id,name} shape (location name = "Event — Location").
export interface VendorOption {
  id: string;
  name: string;
}
export interface LocationOption {
  id: string;
  name: string;
}

// ---------------------------------------------------------------------------
// Reconciliation report — GET /reports/reconciliation.
// Mirror of Distributed-Systems Contracts/Reports/ReconciliationContracts.cs.
// Money fields are integer CENTS; orders/transactions are counts.
// ---------------------------------------------------------------------------

export interface ReconciliationParams {
  eventId: string; // required — reconciliation is event-scoped
  from?: string; // yyyy-MM-dd
  to?: string; // yyyy-MM-dd
  vendorId?: string;
  locationId?: string;
}

// One side's totals (New or OldComparable).
export interface ReconTotals {
  gross: number;
  net: number;
  tax: number;
  tip: number;
  surcharge: number;
  discount: number;
  refunds: number;
  orders: number;
  transactions: number;
}

// One named cause of the gross gap. Mutually exclusive & exhaustive:
// Σ grossDelta == New.gross − OldComparable.gross ; Σ netImpact == New.net − OldComparable.net.
export interface ReconDifference {
  category: string; // e.g. "comp_no_real_money" / "refunded_in_gross"
  label: string;
  rule: string;
  grossDelta: number;
  netImpact: number;
  orders: number;
  transactions: number;
}

// Gross → Net bridge (NEW side): net == gross − tax − discount − refundPreTaxNetting
//   − realMoneyCapReduction − headerVsLineAdjustment.
export interface GrossNetBridge {
  gross: number;
  tax: number;
  discount: number;
  refundPreTaxNetting: number;
  realMoneyCapReduction: number;
  headerVsLineAdjustment: number;
  net: number;
}

// Transactions New counts that OldComparable drops.
// totalCount == compNoRealMoneyCount + refundedOrderCount == New.transactions − OldComparable.transactions.
export interface ExtraTransactions {
  totalCount: number;
  totalValue: number;
  compNoRealMoneyCount: number;
  compNoRealMoneyValue: number;
  refundedOrderCount: number;
  refundedOrderValue: number;
}

export interface ReconEventInfo {
  eventId: string | null;
  eventName: string | null;
  eventLegacyId: number | null;
}

export interface ReconciliationResponse {
  type: ReportType;
  window: WindowInfo;
  scope: ReconEventInfo;
  new: ReconTotals; // == Event Summary for this filter
  oldComparable: ReconTotals; // completed + real-money only (legacy basis)
  grossGap: number; // new.gross − oldComparable.gross (== Σ differences.grossDelta)
  netGap: number; // new.net − oldComparable.net (== Σ differences.netImpact)
  differences: ReconDifference[];
  bridge: GrossNetBridge;
  extra: ExtraTransactions;
}
